import cn from 'classnames';
import PropTypes from 'prop-types';

// JSDoc type definitions
/**
 * @typedef LdsTileProps
 * @property {string} [ariaLabel] - Custom ARIA label value
 * @property {string} [className] - Optional custom user-defined CSS class(es) for the component, will be appended to the list of any other component classes
 * @property {string} [role] - The value of the tile's HTML role attribute
 * @property {React.ReactNode} [children] - React child slot object
 */
/**
 * LdsTile component
 * @param {LdsTileProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element} - LdsTile component
 */
function LdsTile({
  ariaLabel = undefined,
  className = undefined,
  role = undefined,
  children,
  ...rest
}) {
  return (
    <div
      aria-label={ariaLabel}
      className={cn(
        'lds-tile',
        className
      )}
      role={role}
      {...rest}
    >
      {children}
    </div>
  );
}

// JSDoc comments to supply typscript compiler with the necessary types
/**
 * @typedef TileBodyProps
 * @property {string} [className] - Optional custom user-defined CSS class names, will be appended to the list of any other component classes
 * @property {React.ReactNode} [children] - Optional children
 */
/**
 * LdsTile.Body subcomponent
 * @param {TileBodyProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element} - TileBody component
 */
function Body({
  children,
  className,
  ...rest
}) {
  return (
    <div
      className={cn(
        'tile-body',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
LdsTile.Body = Body;

// JSDoc comments to supply typscript compiler with the necessary types
/**
 * @typedef TileFooterProps
 * @property {string} [className] - Optional custom user-defined CSS class names, will be appended to the list of any other component classes
 * @property {React.ReactNode} [children] - Optional children
 */
/**
 * LdsTile.Footer subcomponent
 * @param {TileFooterProps & React.InputHTMLAttributes<HTMLInputElement>} props
 * @returns {React.JSX.Element} - TileFooter component
 */
function Footer({
  children,
  className,
  ...rest
}) {
  return (
    <div
      className={cn(
        'tile-footer',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  );
}
LdsTile.Footer = Footer;

export default LdsTile;

LdsTile.propTypes = {
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  role: PropTypes.string,
};
